import { Box, Row, Txt } from "@stenajs-webui/core";
import {
  Banner,
  Label,
  PrimaryButton,
  SecondaryButton,
} from "@stenajs-webui/elements";
import { CheckboxWithLabel, TextInput } from "@stenajs-webui/forms";
import { Window, WindowFooter } from "@stenajs-webui/modal";
import { cssColor } from "@stenajs-webui/theme";
import { useId, useState } from "react";
import { GapValues } from "@/lib/utils/GapValues";
import { FieldCaption } from "@/components/forms/FieldCaption";
import { RebateOverview, RebatePayoutStatusCode } from "@/gql/graphql";
import { useHandleRebate } from "../hooks/UseHandleRebate";
import { RebateAmountInput } from "./RebateAmountInput";
import { getPayoutGoal } from "../lib/rebatePayoutActions";
import { periodOngoingWarning } from "../lib/rebatePeriodWarning";
import { resolveStatusCodeForPayout } from "../lib/rebatePayoutStatus";
import {
  clampToMax,
  formatAmount,
  formatAmountWithCurrency,
  isValidAllocation,
  normalizeAmountInput,
  parseAmount,
  subtractAmount,
} from "../lib/rebateAmountUtils";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
export type RebateModalMode = "RELEASE" | "PAY" | "PAYONHIGHERLEVEL";

// The backend rejects a payout whose customer reference is longer than this.
const maxLengthOfCustomerReference = 17;

// Shared by every input in the form, so they line up whether or not a
// currency follows them.
const inputWidth = 260;

const getButtonLabel = (statusCode: RebatePayoutStatusCode): string => {
  switch (statusCode) {
    case RebatePayoutStatusCode.Failed:
      return "Set to Failed";
    case RebatePayoutStatusCode.Paid:
      return "Set to Paid";
    case RebatePayoutStatusCode.Negotiated:
      return "Set to Negotiated";
  }
};

const getAmountToPay = (mode: RebateModalMode, reserved: number) =>
  mode !== "RELEASE" ? formatAmount(reserved) : "0";
const getAmountToRelease = (mode: RebateModalMode, reserved: number) =>
  mode === "RELEASE" ? formatAmount(reserved) : "0";

export const HandleRebateFormModal = ({
  isOpen,
  rebate,
  onDone,
  mode,
}: {
  isOpen: boolean;
  rebate?: RebateOverview;
  onDone: () => void;
  mode: RebateModalMode;
}) => {
  const { handleRebate, loading, error, success } = useHandleRebate();

  const goal = rebate ? getPayoutGoal(rebate) : undefined;
  const reserved = rebate?.reservedAmount || 0;
  const currency = rebate?.rebateAgreement?.currencyCode;
  const [amountToPay, setAmountToPay] = useState(
    getAmountToPay(mode, reserved)
  );
  const [amountToRelease, setAmountToRelease] = useState(
    getAmountToRelease(mode, reserved)
  );
  const [customerReference, setCustomerReference] = useState("");
  const customerReferenceCounterId = useId();
  const [manualPricing, setManualPricing] = useState(false);

  const creditSum = parseAmount(amountToPay) || 0;
  const releaseSum = parseAmount(amountToRelease) || 0;
  const statusCodeForPayout = resolveStatusCodeForPayout(
    mode,
    creditSum,
    releaseSum,
    reserved
  );

  const canSubmit = isValidAllocation(amountToPay, amountToRelease, reserved);
  const periodWarning = periodOngoingWarning(mode, goal);

  return (
    <Window
      isOpen={isOpen}
      onRequestClose={onDone}
      headerText="Handle rebate"
      indent={GapValues.TWENTYFOUR}
    >
      <Box
        gap={GapValues.TWENTYEIGHT}
        data-testid={testIdConstants.handleRebateModal}
      >
        {success && (
          <Banner variant="success" text="Rebate handled successfully." />
        )}
        {error && (
          <Banner
            variant="error"
            text={error.message || "Something went wrong."}
          />
        )}
        {!goal && (
          <Banner
            variant="warning"
            text="There is no level to pay out. Set an expected volume that reaches one of the levels first."
          />
        )}
        {!success && periodWarning && (
          <Banner variant="warning" text={periodWarning} />
        )}
        <Label text={"Reserved"}>
          <Txt data-testid={testIdConstants.handleRebateModalReservedValue}>
            {formatAmountWithCurrency(reserved, currency)}
          </Txt>
        </Label>
        <FieldCaption label={"Amount to pay"}>
          <Row gap alignItems={"center"}>
            <Box width={inputWidth}>
              <RebateAmountInput
                ariaLabel={"Amount to pay"}
                dataTestid={testIdConstants.handleRebateModalAmountToPayInput}
                disabled={mode === "RELEASE"}
                value={amountToPay}
                onValueChange={(v) => setAmountToPay(clampToMax(v, reserved))}
                onBlur={() => {
                  setAmountToPay(normalizeAmountInput);
                  setAmountToRelease((prev) =>
                    subtractAmount(amountToPay, reserved, prev)
                  );
                }}
              />
            </Box>
            <Txt>{currency}</Txt>
          </Row>
        </FieldCaption>
        <FieldCaption label={"Amount to release"}>
          <Row gap alignItems={"center"}>
            <Box width={inputWidth}>
              <RebateAmountInput
                ariaLabel={"Amount to release"}
                dataTestid={
                  testIdConstants.handleRebateModalAmountToReleaseInput
                }
                value={amountToRelease}
                onValueChange={(v) =>
                  setAmountToRelease(clampToMax(v, reserved))
                }
                disabled={mode === "RELEASE"}
                onBlur={() => {
                  setAmountToRelease(normalizeAmountInput);
                  setAmountToPay((prev) =>
                    subtractAmount(amountToRelease, reserved, prev)
                  );
                }}
              />
            </Box>
            <Txt>{currency}</Txt>
          </Row>
        </FieldCaption>
        {mode !== "RELEASE" && (
          <>
            <FieldCaption label={"Customer reference"}>
              <Box width={inputWidth} gap>
                <TextInput
                  aria-label={"Customer reference"}
                  aria-describedby={customerReferenceCounterId}
                  data-testid={
                    testIdConstants.handleRebateModalCustomerReferenceInput
                  }
                  maxLength={maxLengthOfCustomerReference}
                  value={customerReference}
                  onValueChange={setCustomerReference}
                />
                <Row justifyContent={"flex-end"}>
                  <Txt
                    id={customerReferenceCounterId}
                    variant={"caption"}
                    size={"small"}
                    color={cssColor("--lhds-color-ui-800")}
                    data-testid={
                      testIdConstants.handleRebateModalCustomerReferenceCounter
                    }
                  >
                    {customerReference.length}/{maxLengthOfCustomerReference}
                  </Txt>
                </Row>
              </Box>
            </FieldCaption>
            <Label text="Payment method">
              <Txt>Credit note</Txt>
            </Label>
            <Box
              alignItems={"flex-start"}
              data-testid={
                testIdConstants.handleRebateModalManualPricingCheckbox
              }
            >
              <CheckboxWithLabel
                value={manualPricing}
                onValueChange={setManualPricing}
                label={"Manual pricing"}
              />
            </Box>
          </>
        )}
      </Box>
      <WindowFooter>
        <Row gap={GapValues.SIXTEEN} justifyContent="flex-end">
          <SecondaryButton
            label="Close"
            onClick={() => onDone()}
            data-testid={testIdConstants.handleRebateModalCloseButton}
          />
          <PrimaryButton
            label={getButtonLabel(statusCodeForPayout)}
            data-testid={testIdConstants.handleRebateModalSubmitButton}
            onClick={() => {
              if (!goal || !rebate) return;
              handleRebate({
                rebateAgreementId: rebate.rebateAgreementId,
                criteriaId: goal.criteriaId,
                goalId: goal.id,
                goalValidFrom: goal.validFrom,
                goalValidTo: goal.validTo,
                period: goal.period,
                creditSum,
                releaseSum,
                statusCodeForPayout,
                customerReference: customerReference || undefined,
                manualPricing,
              });
            }}
            loading={loading}
            disabled={success || !canSubmit || !goal}
          />
        </Row>
      </WindowFooter>
    </Window>
  );
};
